/**
 * Applying custom lexicons to the open document (VE-24948 / U5).
 *
 * This saga is the WIRING ONLY. Every decision with a correctness argument
 * behind it lives in a pure function next door, and that split is deliberate:
 *
 *   - matching            → `lexiconMatchWorker` (offsets only, ADR-002)
 *   - the three budgets   → `lexiconMatchWorkerClient` (ADR-001)
 *   - offsets → geometry  → `resolveLexiconMatchGeometry` (`glyphSpanGeometry`)
 *   - the merge (D13)     → `mergeLexiconMatches`
 *   - code id → stamp     → `redactionCodeAssignmentFor`
 *
 * The one thing this file resolves itself is WHICH redaction codes the run
 * needs (`resolveEntryRedactionCodes`), because that is a question about the
 * loaded lexicons rather than a rule about them.
 *
 * So the whole merge matrix, every budget tier and the wrapped-match geometry
 * are testable without a store, a worker or a PDF, and what remains here is
 * dispatch, session reads and one channel.
 *
 * ── Two entry points, one path (BR-49 / BR-50) ───────────────────────────────
 *
 * Upload (`U5-FR14`) persists a selection and evaluates NOTHING: matching
 * consumes extracted page text, which does not exist until the editor opens the
 * document. First open sees a record with no `evaluatedAt` and evaluates it.
 * The editor panel (`U5-FR18`) evaluates immediately — EXCEPT while OCR is
 * still making the document readable, where it records the selection and defers
 * exactly as the upload path does. Both converge here.
 *
 * That deferral makes evaluation WAIT ON THE ENGINE POLLER, which is an
 * ordering dependency worth stating because it is not obvious from here: a
 * fresh session starts at `ocrEngineState: 'checking'` and the gate refuses
 * there, so no lexicon matches until the poller's first read answers. The
 * single watcher below therefore listens for every way that state can settle,
 * not just for text — whichever of the engine and the index finishes last is
 * what wakes the run.
 *
 * ── Why re-evaluation is session-scoped ──────────────────────────────────────
 *
 * Page text arrives progressively (react-pdf lazy-renders; the pre-pass lands in
 * one batch), so an apply run while only the first pages are extracted must be
 * extended to the rest — reporting "0 matches" over a partially extracted
 * document is a defect (BR-48). That extension re-reads the LIVE lexicon, so it
 * is confined to the session that applied it, tracked in
 * `lexiconApply.sessionAppliedLexiconIds`.
 *
 * While the pre-pass streams, a lexicon that already has results is re-run
 * once when the index finishes, not per chunk (`hasCurrentEvaluation`).
 *
 * On reopen, lexicon detections are restored whole from the saved asset
 * (`lexiconDetectionsFromAssetBody`) and nothing is re-evaluated — if it were, a
 * lexicon edited after application would reach back into an already-applied
 * document, which is what `U5-FR2` / BR-44 forbid.
 *
 * ── The one exception, and what it costs ─────────────────────────────────────
 *
 * A record also re-evaluates when the document is no longer the FILE it was
 * evaluated against (`evaluatedAssetId` vs `session.mediaAssetId`). That exists
 * for OCR: a lexicon applied to a scanned document matches nothing, stamps
 * `evaluatedAt`, and every other rule then passes it over — so the reload onto
 * OCR's searchable copy left a zero-match result standing over a document that
 * by then had text.
 *
 * BE CLEAR ABOUT THE TENSION. This is re-evaluation outliving the session, and
 * it re-reads the LIVE lexicon like every other re-evaluation. A lexicon edited
 * between the first run and the reload therefore does reach into an
 * already-applied document, which is the thing the paragraph above says must
 * not happen. It is accepted here because the window is narrow and the
 * alternative is worse: an asset change means OCR republished, which happens
 * once and soon after upload, and the standing result it would otherwise leave
 * is a silent under-redaction (NFR-S3) — the one failure direction this feature
 * cannot take. The record does not lie about it either: `buildAppliedRecords`
 * restamps `lexiconName` and `revision` from the lexicon actually read.
 *
 * If that trade stops being acceptable, the fix is to re-evaluate against the
 * revision the record froze rather than the live one, which needs a
 * revision-pinned fetch the lexicon service does not have today.
 *
 * ── A second exception: records evaluated during indexing ────────────────────
 *
 * A record marked `incomplete.indexing` (evaluated before the text index
 * finished) re-runs on the next open. Same trade and cost as above; reachable
 * only if it was saved mid-index and the completing run never landed.
 */

import { END, buffers, eventChannel } from 'redux-saga';
import {
  call,
  delay,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';
import { modules } from '@veritone/glc-redux';
import { v4 as uuid } from 'uuid';

import {
  APPLY_LEXICONS_TO_OPEN_DOCUMENT,
  LEXICON_APPLY_AWAITING_TEXT,
  LEXICON_APPLY_FAILURE,
  LEXICON_APPLY_PROGRESS,
  LEXICON_APPLY_STARTED,
  LEXICON_APPLY_SUCCESS,
  CONSUME_UPLOAD_LEXICON_BATCH_FILE,
  OPEN_DOCUMENT_SUCCESS,
  PDF_TEXT_PREPASS_DONE,
  SET_DETECTIONS_TAB,
  OCR_FAILED,
  OCR_INDETERMINATE,
  OCR_MANIFEST_LOADED,
  OCR_NOT_RUN,
  OCR_PROCESSING,
  OCR_REFRESH_AVAILABLE,
  OCR_RELOAD_SETTLED,
  OCR_TIMED_OUT,
  PAGE_SEARCHABILITY_SCAN_DONE,
  SET_PAGE_TEXT,
  SET_PAGE_TEXT_BATCH,
  RECORD_UPLOAD_LEXICON_SELECTION,
  SET_PAGE_TEXT_ITEMS,
  SET_REDACTIONS_SUBTAB,
} from '../actions';
import {
  selectPiiRunGate,
  selectSession,
  selectUploadLexiconBatch,
} from '../selectors';
import {
  AppliedLexiconRecord,
  hasIncompleteCause,
  LexiconApplyErrorCode,
  RedactionCodeAssignment,
  SessionState,
} from '../models';
import { toPageRanges } from '../pageRanges';
import { redactionsToAssetBody } from '../helpers';
import {
  RedactionAssetWriter,
  serviceSaveDocumentRedactionData,
} from '../services';
import { selectGraphQLConfig, selectToken } from './auth';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { TDOId } from '@common-modules/universal/models/Brands';
import {
  LexiconMatchWithText,
  lexiconIdByEntryId,
  lexiconNamesForEntries,
  mergeLexiconMatches,
  resolveLexiconMatchGeometry,
} from '../lexiconApply';
import {
  LexiconMatchOutcome,
  LexiconMatchPage,
  LexiconMatchProgress,
  LexiconMatchSpan,
  runLexiconMatch,
} from '../lexiconMatchWorkerClient';
import {
  loadLexiconsByIds,
  selectLexiconsById,
} from '@redact-modules/lexicons';
import {
  Lexicon,
  LexiconEntry,
  liveEntries,
} from '@redact-modules/lexicons/models';
import { ensureRedactionCodesLoaded } from '@redact-modules/redactionCodes/sagas';
import { redactionCodeAssignmentFor } from '../redactionCodeAssignment';

const {
  user: { selectUser },
} = modules;

/**
 * Coalescing delay for page-text arrival, matching `keywordSearch`'s.
 *
 * react-pdf can fire a burst of SET_PAGE_TEXT as pages render; one re-evaluation
 * after the burst settles is the point.
 */
const PAGE_TEXT_SETTLE_MS = 150;

/**
 * Minimum gap between `LEXICON_APPLY_PROGRESS` dispatches, ms. The first page
 * still reports immediately (NFR-U5-6).
 *
 * Per-page dispatch re-rendered the whole editor (it subscribes to `session`)
 * and the page loop waited on it: ~19 ms/page vs ~0.05 ms of matching (VE-29502).
 */
const PROGRESS_FLUSH_MS = 250;

/**
 * Pages this run can both MATCH and PLACE, in document order (BR-48).
 *
 * Gated on `pageTextItems` as well as `pageTexts`, because the resolver needs
 * both: text alone yields matches whose geometry cannot be computed, and
 * `resolveMatchGeometry` then dropped the whole page's match set — silently,
 * and reported as a plain "No matches found." over a document that did match
 * (review-gate B9). A page with text but no items is not skipped forever; the
 * items action is watched below, so it is picked up when they land.
 */
function pagesWithText(session: SessionState): LexiconMatchPage[] {
  const pageTexts = session.pageTexts ?? {};
  const pageItems = session.pageTextItems ?? {};
  return Object.keys(pageTexts)
    .map((p) => Number(p))
    .filter((p) => Number.isFinite(p) && p > 0)
    .sort((a, b) => a - b)
    .map((page) => ({ page, text: pageTexts[page] ?? '' }))
    .filter((p) => p.text.length > 0 && (pageItems[p.page]?.length ?? 0) > 0);
}

/**
 * Pages the text index has not reached, by number (VE-29686).
 *
 * The page count comes from the pre-pass's own `total`, MuPDF's reading of the
 * catalog. NOT `session.totalPages`: that is an open-time seed of 1 that
 * nothing writes back (`keywordSearch` documents the same trap), so a list
 * built on it was empty on every document.
 *
 * Unindexed means "no `pageTexts` entry", not "no text": a blank or scanned
 * page WAS indexed and found empty, and the "no searchable text" warning is
 * what reports those. Listing them here would name every scanned page as a
 * page the lexicon never reached.
 *
 * `null` when the count is NOT KNOWN: no pre-pass chunk has landed yet, or the
 * pre-pass failed before its first. That is not "no pages missed", and the
 * caller records it as `pageCountUnknown` rather than as an empty list.
 */
export function unindexedPageNumbers(session: SessionState): number[] | null {
  const total = session.textIndexProgress?.total;
  if (total === undefined) {
    return null;
  }
  const pageTexts = session.pageTexts ?? {};
  const unindexed: number[] = [];
  for (let page = 1; page <= total; page += 1) {
    if (pageTexts[page] === undefined) {
      unindexed.push(page);
    }
  }
  return unindexed;
}

type MatchEvent =
  | { kind: 'progress'; progress: LexiconMatchProgress }
  | { kind: 'done'; outcome: LexiconMatchOutcome }
  | { kind: 'error'; message: string }
  | { kind: 'tick' };

/**
 * Wraps one matcher run as a channel so per-page progress can be dispatched
 * while the run is still going (`NFR-U5-5`).
 *
 * A bare `yield* call(runLexiconMatch, …)` would only surface the final outcome,
 * and on a long document the panel would sit silent for up to the whole 30 s
 * apply budget. An expanding buffer because progress can be emitted while the
 * saga is not parked at `take`.
 *
 * Closing the channel aborts the run and terminates its worker — on saga
 * cancellation, or when `runLexiconApply` leaves its loop early. The reducer
 * still drops any outcome whose `requestId` is not current.
 */
function makeMatchChannel(args: {
  requestId: string;
  entries: readonly LexiconEntry[];
  pages: readonly LexiconMatchPage[];
}) {
  return eventChannel<MatchEvent>((emit) => {
    const controller = new AbortController();
    // One timer per run (not per page): flushes progress and notices a
    // close/supersession while the matcher is quiet.
    const tick = setInterval(() => emit({ kind: 'tick' }), PROGRESS_FLUSH_MS);
    // An async IIFE rather than a `.then` chain, mirroring `pdfDimsPrepass`.
    void (async () => {
      try {
        const outcome = await runLexiconMatch({
          requestId: args.requestId,
          entries: args.entries,
          pages: args.pages,
          onProgress: (progress) => emit({ kind: 'progress', progress }),
          signal: controller.signal,
        });
        emit({ kind: 'done', outcome });
      } catch (error: unknown) {
        emit({
          kind: 'error',
          // The message may not name a term, but it is not guaranteed not to
          // either, so it never reaches a log — only the failure action, whose
          // copy the UI owns (SEC-R3 / NFR-S2).
          message:
            error instanceof Error ? error.message : 'Lexicon matching failed',
        });
      } finally {
        clearInterval(tick);
        emit(END);
      }
    })();
    // Closing the channel stops the run; a no-op once it has finished.
    return () => {
      clearInterval(tick);
      controller.abort();
    };
  }, buffers.expanding<MatchEvent>());
}

/**
 * One applied-lexicon record per lexicon in this run.
 *
 * `lexiconName` and `revision` are captured AS AT this application, so a
 * reviewer can later see they applied v3 while settings now holds v5, and a
 * rename cannot retroactively relabel what they applied (`U5-FR2`).
 */
function buildAppliedRecords(args: {
  lexicons: readonly Lexicon[];
  source: 'upload' | 'editor' | 'auto';
  actor: string;
  now: string;
  evaluated: boolean;
  /** The `media` asset matching read — see the record's `evaluatedAssetId`. */
  evaluatedAssetId?: string | null;
  /** Records already on the session, so `source` is not restamped (BR-51). */
  existing?: readonly AppliedLexiconRecord[];
  /**
   * What THIS lexicon could not finish, or `undefined` if it finished cleanly.
   *
   * A function rather than a value: the run-wide facts (a budget stop, a dead
   * worker, an unsearched page) belong to every record, but an abandoned entry
   * and an unplaceable match belong to the lexicon that owns the entry. Passing
   * one object marked every lexicon in the run with one lexicon's failure.
   */
  incompleteFor?: (lexiconId: string) => AppliedLexiconRecord['incomplete'];
  /** False for a flags-only rewrite: keep the prior `appliedAt`/`appliedBy`. */
  restamp?: boolean;
}): AppliedLexiconRecord[] {
  return args.lexicons.map((lexicon) => {
    const prior = args.existing?.find((r) => r.lexiconId === lexicon.id);
    const keepStamp = args.restamp === false && prior;
    return {
      lexiconId: lexicon.id,
      lexiconName: lexicon.name,
      revision: lexicon.revision,
      appliedAt: keepStamp ? prior.appliedAt : args.now,
      appliedBy: keepStamp ? prior.appliedBy : args.actor,
      // How the lexicon was SELECTED is a fact about the selection, not about
      // whichever run happened to evaluate it. Re-evaluation used to overwrite
      // an upload selection with 'editor' for 100% of upload-path applies.
      source: prior?.source ?? args.source,
      evaluatedAt: args.evaluated ? args.now : null,
      // Stamped only alongside a real evaluation. A deferred one has read
      // nothing, so it must not claim to have read a file.
      evaluatedAssetId: args.evaluated ? (args.evaluatedAssetId ?? null) : null,
      // Durable incompleteness (B3). Only ever SET here, never cleared by a
      // later run that was itself incomplete — a clean run clears it by
      // omitting the key, which is what makes re-applying a real remedy. A run
      // that was incomplete FOR ANOTHER LEXICON clears it too, which is the
      // point of asking per lexicon.
      ...(() => {
        const mark = args.incompleteFor?.(lexicon.id);
        return mark ? { incomplete: mark } : {};
      })(),
    };
  });
}

/**
 * Mark requested lexicons that could NOT be loaded as evaluated.
 *
 * A lexicon DELETED between selection and first open comes back in the loader's
 * `missingIds` — this handles that case only; an unreachable one is reported and
 * left retryable before we ever get here. Leaving a deleted lexicon's record with
 * `evaluatedAt: null` makes `watchFirstOpen` re-run the whole apply on every
 * open forever — exactly what the "evaluated: true" comment below says it is
 * preventing. Carrying the EXISTING record forward keeps the name as applied
 * (U5-FR2); a requested id with no record at all has nothing to preserve and is
 * simply dropped.
 */
function resolveUnloadedAsEvaluated(args: {
  existing: readonly AppliedLexiconRecord[];
  requestedIds: readonly string[];
  loaded: readonly Lexicon[];
  now: string;
}): AppliedLexiconRecord[] {
  const loadedIds = new Set(args.loaded.map((l) => l.id));
  return args.requestedIds
    .filter((id) => !loadedIds.has(id))
    .flatMap((id) => {
      const record = args.existing.find((r) => r.lexiconId === id);
      if (!record) {
        return [];
      }
      // Drop `indexing` (nothing can finish a deleted lexicon's run; it would
      // re-run every open) but keep the pages it says went unsearched.
      if (!record.incomplete) {
        return [{ ...record, evaluatedAt: args.now }];
      }
      const { indexing: _indexing, ...incomplete } = record.incomplete;
      if (record.incomplete.indexing !== true) {
        // A mark that never had `indexing` is left exactly as it was written.
        return [{ ...record, evaluatedAt: args.now, incomplete }];
      }
      // A mark written mid-index BEFORE VE-29686 never listed its unindexed
      // pages: the list was built from the `totalPages` seed of 1, so it was
      // always empty. Its coverage is unknown, not clean, and dropping it
      // would autosave a clean record over pages nobody searched. Such marks
      // are told apart by carrying NEITHER page field: `incompleteFor` now
      // always writes `unsearchedPageRanges` while indexing, even empty.
      //
      // The legacy clause is DEFENCE IN DEPTH, not a live path.
      // `appliedLexiconsFromAssetBody` is the only way a record reaches this
      // session and it migrates the key away, so in production the second
      // test can no longer fire. It stays because the cost is one `&&` and
      // the failure it guards against — misreading a legacy mark as clean and
      // autosaving over pages nobody searched — is the one this product can
      // least afford to take on trust in a single function.
      if (
        incomplete.unsearchedPageRanges === undefined &&
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        incomplete.unsearchedPages === undefined &&
        incomplete.pageCountUnknown !== true
      ) {
        return [
          {
            ...record,
            evaluatedAt: args.now,
            incomplete: { ...incomplete, pageCountUnknown: true },
          },
        ];
      }
      // If `indexing` was the only cause, nothing is left to report. The rest
      // object still carries the required fields, so it would stay truthy and
      // show an "incomplete" chip naming no cause (VE-29686).
      if (!hasIncompleteCause(incomplete)) {
        const { incomplete: _dropped, ...clean } = record;
        return [{ ...clean, evaluatedAt: args.now }];
      }
      return [{ ...record, evaluatedAt: args.now, incomplete }];
    });
}

/**
 * Resolves the redaction codes the applicable entries reference (`U5-FR8`).
 *
 * Skips the load entirely when no entry names a code, which is the common
 * case — an apply must not pay for a settings fetch it has no use for.
 *
 * Returns a map keyed by code id. Ids that do not resolve are simply absent:
 * a code deleted after an entry referenced it applies uncoded rather than
 * failing the match (BR-15), and the merge treats a missing entry the same way
 * it treats an entry with no code at all.
 */
function* resolveEntryRedactionCodes(lexicons: readonly Lexicon[]) {
  const wanted = new Set<string>();
  for (const lexicon of lexicons) {
    for (const entry of liveEntries(lexicon.entries)) {
      if (entry.redactionCodeId) {
        wanted.add(entry.redactionCodeId);
      }
    }
  }
  const resolved = new Map<string, RedactionCodeAssignment>();
  if (wanted.size === 0) {
    return resolved;
  }
  const codes = yield* call(ensureRedactionCodesLoaded);
  for (const id of wanted) {
    const code = codes[id];
    if (code) {
      resolved.set(id, redactionCodeAssignmentFor(code));
    }
  }
  return resolved;
}

export function* runLexiconApply(payload: {
  lexiconIds: readonly string[];
  source: 'upload' | 'editor' | 'auto';
  /**
   * Whether to move the reviewer to the results.
   *
   * Deliberately separate from `source`: `source` records how the lexicon was
   * SELECTED (BR-51) and is persisted, while this is about who asked for THIS
   * run. Re-evaluation on page-text arrival used to pass `source: 'editor'` to
   * mean "not the upload path", which both mis-stamped the persisted record and
   * yanked the tab on the upload path the comment below says it must not
   * (review finding 3).
   */
  navigate?: boolean;
}) {
  const opened: SessionState | null = yield* select(selectSession);
  if (!opened) {
    return;
  }
  const tdoId = opened.tdoId;

  const requestId = uuid();
  const lexiconIds = [...new Set(payload.lexiconIds)];
  if (lexiconIds.length === 0) {
    return;
  }

  // Does this run redact what it finds? Read from the document's OWN applied
  // records — the picker wrote the choice per lexicon at upload — and only for
  // the lexicons this run is actually applying.
  const willAutoRedact = opened.appliedLexicons.some(
    (record) =>
      record.autoRedact === true && lexiconIds.includes(record.lexiconId)
  );

  // STARTED first, so the ids join `sessionAppliedLexiconIds` even on the paths
  // that bail below — otherwise an apply requested before any text was
  // extracted would never be picked up when the text arrives (BR-48).
  yield* put(
    LEXICON_APPLY_STARTED({
      requestId,
      lexiconIds,
      ...(willAutoRedact ? { autoRedact: true } : {}),
    })
  );

  /**
   * OCR IS STILL MAKING THIS DOCUMENT READABLE — record the selection, defer
   * the matching.
   *
   * Matching now would read the pre-OCR file, find nothing, and stamp the
   * record evaluated, which is the silent under-redaction this whole path is
   * written against (NFR-S3). Deferred is a state the feature already has and
   * already recovers from: the ids joined `sessionAppliedLexiconIds` on
   * STARTED above, so `pendingLexiconIds` keeps them and the watchers below
   * re-run once the gate opens.
   *
   * AFTER `LEXICON_APPLY_STARTED`, deliberately — that is what records the ids.
   * Bailing before it would drop the selection on the floor.
   *
   * This covers the auto-apply path as well as the button, because both arrive
   * through `APPLY_LEXICONS_TO_OPEN_DOCUMENT`; the button's own disabled state
   * is the visible half, and this is the half that holds when something else
   * dispatches.
   */
  const gate = yield* select(selectPiiRunGate);
  if (gate !== 'open') {
    yield* put(LEXICON_APPLY_AWAITING_TEXT({ requestId }));
    return;
  }

  const user = (yield* select((s: unknown) => selectUser(s as never))) as
    | { email?: string }
    | undefined;
  const actor = user?.email ?? '';
  const now = new Date().toISOString();

  try {
    const load = yield* call(loadLexiconsByIds, lexiconIds);
    const lexicons = load.lexicons;
    // A lexicon we could not FETCH is not a lexicon with no matches.
    //
    // This is reported and abandoned rather than merged, and the records are
    // deliberately left unevaluated so the next open (or the next page of text)
    // retries. Previously the failure was swallowed in the loader: the run
    // continued with the lexicons that did load, reported its count as the
    // whole answer, and `resolveUnloadedAsEvaluated` then stamped the failed
    // one as evaluated — so ONE transient fetch error permanently converted an
    // applied lexicon into "0 matches", with nothing on screen to say so. That
    // is a silent under-redaction, which is the one failure direction this
    // feature cannot take (NFR-S3).
    //
    // Bailing on the whole run, rather than proceeding with the rest, is the
    // conservative reading: a partial apply presented as a complete one is the
    // same lie in a smaller font.
    if (load.unreachableIds.length > 0) {
      yield* put(
        LEXICON_APPLY_FAILURE({
          requestId,
          message: 'lexiconUnreachable' satisfies LexiconApplyErrorCode,
        })
      );
      return;
    }
    // BR-4 — an empty lexicon is a legal intermediate state but applying it is a
    // silent no-op, which reads to the user as a bug.
    const applicable = lexicons.filter(
      (l) => liveEntries(l.entries).length > 0
    );
    if (applicable.length === 0) {
      // Re-read the session, for the same reason the main path does below:
      // `loadLexiconsByIds` awaited N round trips, and `LEXICON_APPLY_SUCCESS`
      // REPLACES the whole detection list. Writing back the pre-await snapshot
      // silently reverted anything that landed meanwhile — a PII_RESULTS_LOADED,
      // a manual redaction, a dismissal — which is the data-loss shape of
      // review finding 5, running in the opposite direction.
      const settled: SessionState | null = yield* select(selectSession);
      if (!settled || settled.tdoId !== tdoId) {
        return;
      }
      yield* put(
        LEXICON_APPLY_SUCCESS({
          requestId,
          detections: settled.detections,
          appliedLexicons: [
            ...buildAppliedRecords({
              lexicons,
              source: payload.source,
              actor,
              now,
              existing: settled.appliedLexicons,
              // Nothing to match, so the record IS resolved: leaving it
              // unevaluated would re-run this on every open forever.
              evaluated: true,
              // An empty lexicon reads the same on any file, but the record
              // still names the one it settled against — otherwise it reads as
              // stale on every open and re-runs exactly as often.
              evaluatedAssetId: settled.mediaAssetId,
            }),
            ...resolveUnloadedAsEvaluated({
              existing: settled.appliedLexicons,
              requestedIds: lexiconIds,
              loaded: lexicons,
              now,
            }),
          ],
          matchCount: 0,
          // Says which zero this is. An empty lexicon reported the same "No
          // matches found." as a lexicon that genuinely matched nothing, so a
          // CSV import that never landed its entries looked identical to a
          // working lexicon on a document with no hits — and the one person who
          // could fix it had no way to tell.
          disposition: {
            alreadyPresent: 0,
            folded: 0,
            dismissed: 0,
            unattributed: 0,
            emptyLexicons: lexicons.length,
            unresolvedGeometry: 0,
          },
          abandonedEntryIds: [],
          timedOut: false,
          pagesMatched: [],
          // Adds no detections, but it does resolve the applied records — which
          // is what stops `watchFirstOpen` re-running forever.
          changed: true,
        })
      );
      return;
    }

    // Resolved BEFORE matching, not just before the merge, even though only
    // the merge uses it. The merge runs against a session re-read taken after
    // matching precisely so nothing the reviewer did meanwhile is reverted;
    // putting a fetch between that re-read and the merge would reopen the
    // window it exists to close.
    const entryRedactionCodes = yield* call(
      resolveEntryRedactionCodes,
      applicable
    );

    // Re-read, for the same reason the two adjacent paths do (see their own
    // comments): `loadLexiconsByIds` awaited N round trips, and text that
    // arrived during them is in the live session but not in `opened`. Matching
    // the stale snapshot made every first open of a large document do its work
    // twice, and reported `awaiting-text` for text that had already landed
    // (review-gate M8).
    const afterLoad: SessionState | null = yield* select(selectSession);
    if (!afterLoad || afterLoad.tdoId !== tdoId) {
      return;
    }
    const pages = pagesWithText(afterLoad);
    // Did THIS run's input cover the document? Read with `pages`.
    const stillIndexing = !afterLoad.textIndexReady;
    const indexFailed = afterLoad.textIndexFailed === true;
    const indexing = stillIndexing || indexFailed;
    if (pages.length === 0) {
      // Selection recorded, evaluation deferred. NOT reported as "0 matches":
      // that would tell the user their lexicon does not work (BR-48).
      yield* put(LEXICON_APPLY_AWAITING_TEXT({ requestId }));
      return;
    }

    // THE MATCHER'S INPUT, and the single most important `liveEntries` call in
    // the app: a tombstone that reaches this line is a term the agency
    // deliberately deleted, still redacting their documents.
    const entries: LexiconEntry[] = applicable.flatMap((l) =>
      liveEntries(l.entries)
    );

    const channel = makeMatchChannel({ requestId, entries, pages });
    let outcome: LexiconMatchOutcome | null = null;
    let failure: string | null = null;
    // Progress not yet dispatched; see `PROGRESS_FLUSH_MS`.
    let pendingPages: number[] = [];
    let pendingMatchCount = 0;
    let lastFlushAt: number | null = null;
    /** Closed, or superseded by a run from any watcher. */
    let stopped = false;
    try {
      // BREAK on the terminal event; never fall through to the channel's END.
      // `take` on an ended channel TERMINATES the taking saga (that is what
      // makes `pdfDimsPrepass`'s loop exit), so everything after this block
      // would be dead code — the merge below would never run.
      let taking = true;
      while (taking) {
        // Plain `take`: runs once per page on the path the matcher waits on.
        const event = yield* take(channel);
        if (event.kind === 'progress') {
          pendingPages.push(event.progress.page);
          pendingMatchCount += event.progress.matches.length;
        } else {
          if (event.kind === 'done') {
            outcome = event.outcome;
            taking = false;
          } else if (event.kind === 'error') {
            failure = event.message;
            taking = false;
          }
          // Closed (no session) or superseded (another `requestId`, incl. a
          // completed switch). Not SWITCH_DOCUMENT_STARTED: a switch can fail.
          const live: SessionState | null = yield* select(selectSession);
          if (live?.lexiconApply.requestId !== requestId) {
            stopped = true;
            break;
          }
        }
        const at = Date.now();
        // Always flush on a tick and on the terminal event.
        const due =
          !taking ||
          event.kind === 'tick' ||
          lastFlushAt === null ||
          at - lastFlushAt >= PROGRESS_FLUSH_MS;
        if (due && pendingPages.length > 0) {
          yield* put(
            LEXICON_APPLY_PROGRESS({
              requestId,
              pages: pendingPages,
              matchCount: pendingMatchCount,
            })
          );
          pendingPages = [];
          pendingMatchCount = 0;
          lastFlushAt = at;
        }
      }
    } finally {
      channel.close();
    }
    if (stopped) {
      return;
    }

    const done: LexiconMatchOutcome | null = outcome;
    // Partial and lists no gaps: never merge it (NFR-S3).
    if (done?.aborted) {
      return;
    }
    if (failure !== null || !done) {
      yield* put(
        LEXICON_APPLY_FAILURE({
          requestId,
          message: failure ?? 'Lexicon matching produced no result',
        })
      );
      return;
    }

    // Re-read the session: matching is asynchronous, and the reviewer may have
    // drawn, deleted or dismissed something while it ran. Merging against the
    // stale snapshot would silently revert that work — the exact thing M1 exists
    // to prevent.
    const reread: SessionState | null = yield* select(selectSession);
    if (!reread || reread.tdoId !== tdoId) {
      return;
    }
    const current: SessionState = reread;

    const {
      resolved,
      unresolved: unresolvedGeometry,
      unresolvedByEntry,
    } = resolveMatchGeometry(current, done);
    // AUTO REDACT comes from the document's OWN applied records, not from the
    // library. The upload picker wrote the choice per lexicon when the file was
    // staged; a flag flipped in settings since must not change the account of
    // what this document's run did (U5-FR2), and reading the library here would
    // let it.
    //
    // CANCELLED IS READ HERE, at the last possible moment, which is what makes
    // Cancel mean "skip the redaction" rather than "stop the search". The
    // matching has already finished; the reviewer's answer only has to arrive
    // before the matches are promoted, and pressing it after that does nothing
    // rather than half-undoing a run.
    const autoRedactLexiconIds = current.lexiconApply.autoRedact.cancelled
      ? new Set<string>()
      : new Set(
          current.appliedLexicons
            .filter((record) => record.autoRedact === true)
            .map((record) => record.lexiconId)
        );
    const merged = mergeLexiconMatches({
      matches: resolved,
      lexicons: applicable,
      existingDetections: current.detections,
      existingRedactions: current.redactions,
      redactionCodes: entryRedactionCodes,
      autoRedactLexiconIds,
    });

    // Did this run add anything the session did not already have? Every match
    // was either already present, dismissed by the reviewer, or adopted by M2 →
    // provable no-op. See `changed` on the action.
    const addedMatchCount =
      resolved.length -
      merged.alreadyPresentMatchIds.length -
      merged.skippedDismissedMatchIds.length -
      merged.foldedMatchIds.length;
    const evaluatedIds = new Set(
      current.appliedLexicons
        .filter((r) => r.evaluatedAt)
        .map((r) => r.lexiconId)
    );
    // Over the whole REQUESTED set, not just `applicable`. A run that loaded a
    // lexicon with no entries, or could not load one at all, still has to
    // settle that lexicon's record — otherwise `watchFirstOpen` re-runs a full
    // apply on every open forever, autosaving a new asset version each time
    // with no user action (review-gate B5).
    const recordsAreNew = lexiconIds.some((id) => !evaluatedIds.has(id));
    const changed = addedMatchCount > 0 || recordsAreNew;

    // What this run could not finish, made durable on the records (B3). A
    // clean run reports `undefined`, which CLEARS a previous run's flag —
    // re-applying is the remedy, so it has to be able to succeed.
    // Pages nobody searched: a deadline elapsed, or the worker died on them.
    // Both are "unsearched" to a reviewer, and naming them is what decides
    // between re-applying and re-opening (review-gate #3 BR-11).
    // `?? []` deliberately. These fields cross a module boundary, and a
    // spread of `undefined` throws — which inside this saga would turn a run
    // that merely did not FINISH into a run that FAILED, discarding the
    // matches it did produce. The whole point of reporting incompleteness is
    // to keep partial results and say they are partial.
    const unsearchedPages = [
      ...new Set([
        ...(done.timedOutPages ?? []),
        ...(done.crashedPages ?? []),
        ...(done.unreachedPages ?? []),
      ]),
    ].sort((a, b) => a - b);
    // Pages not indexed when this run read its input. Durable record only:
    // while streaming, the panel and audit just say "still indexing".
    // Read from `afterLoad`, the snapshot `pages` was taken from: pages that
    // indexed while matching ran were not in this run's input.
    const unindexed = indexing ? unindexedPageNumbers(afterLoad) : [];
    const pageCountUnknown = unindexed === null;
    const unindexedPages = unindexed ?? [];
    const recordUnsearchedPages = [
      ...new Set([...unsearchedPages, ...unindexedPages]),
    ].sort((a, b) => a - b);
    //
    // PER LEXICON, not per run. This used to be ONE object stamped onto every
    // record in the run, so a single entry with a runaway pattern in one
    // lexicon marked all four applied lexicons incomplete — three of them
    // about work that had finished, and the fourth indistinguishable from
    // them. The warning appeared everywhere and therefore meant nothing.
    //
    // Which facts are whose:
    //   - a budget stop, a dead worker and an unsearched page are RUN-WIDE.
    //     A page nobody read is a gap in every lexicon's coverage of this
    //     document, so every record carries them, and that is not the bug.
    //   - an abandoned entry and a match that could not be placed belong to
    //     the ENTRY that produced them, and so to its lexicon. Those are now
    //     charged to that lexicon alone.
    //
    // An entry that resolves to no lexicon (`unattributedEntryIds`) is charged
    // to nobody — see `countEntriesByLexicon`. It still reaches the panel as
    // this run's abandoned count; it just does not brand a lexicon that may
    // have had nothing to do with it.
    const runWideIncomplete =
      done.timedOut ||
      (done.crashed ?? false) ||
      unsearchedPages.length > 0 ||
      indexing;
    const ownerOfEntry = lexiconIdByEntryId(lexicons);
    const chargeLexicon = (
      tally: Map<string, number>,
      entryId: string,
      count: number
    ) => {
      const lexiconId = ownerOfEntry.get(entryId);
      if (lexiconId !== undefined) {
        tally.set(lexiconId, (tally.get(lexiconId) ?? 0) + count);
      }
    };
    const abandonedByLexicon = new Map<string, number>();
    for (const entryId of done.abandonedEntryIds) {
      chargeLexicon(abandonedByLexicon, entryId, 1);
    }
    const unresolvedByLexicon = new Map<string, number>();
    unresolvedByEntry.forEach((count, entryId) => {
      chargeLexicon(unresolvedByLexicon, entryId, count);
    });
    const incompleteFor = (
      lexiconId: string
    ): AppliedLexiconRecord['incomplete'] => {
      const abandonedEntryCount = abandonedByLexicon.get(lexiconId) ?? 0;
      const unresolvedGeometryCount = unresolvedByLexicon.get(lexiconId) ?? 0;
      if (
        !runWideIncomplete &&
        abandonedEntryCount === 0 &&
        unresolvedGeometryCount === 0
      ) {
        return undefined;
      }
      const mark: NonNullable<AppliedLexiconRecord['incomplete']> = {
        timedOut: done.timedOut,
        ...(done.crashed ? { crashed: true } : {}),
        abandonedEntryCount,
        unresolvedGeometryCount,
        // RANGES, never the flat `unsearchedPages` — that field is read-only
        // legacy now (see its `@deprecated` note). This list is persisted and
        // is document-length on a failed index.
        //
        // NOT DUAL-WRITTEN, deliberately. A build rolled back past VE-29686
        // reads this record's chip and every other cause correctly, but its
        // tooltip prints "Page(s) not searched: none" — the page list degrades
        // and is rebuilt by the next apply. Writing both fields for a release
        // would buy that back at the cost of the entire reason for the change.
        // (It is NOT a false-clean: the vacuous-mark drop that could discard a
        // causeless mark arrived with VE-29686 itself, so no older build has
        // it.)
        //
        // Always present while indexing, even empty: it is what tells a mark
        // written by this code from one written before VE-29686, which never
        // listed its pages (see `resolveUnloadedAsEvaluated`).
        ...(recordUnsearchedPages.length > 0 || stillIndexing
          ? { unsearchedPageRanges: toPageRanges(recordUnsearchedPages) }
          : {}),
        ...(pageCountUnknown ? { pageCountUnknown: true } : {}),
        // Carried so the REMEDY can be right, not as a cause — see the field's
        // note. A gap left by a dead index is the one "Apply again" cannot
        // close, and the part-way case (known count, listed tail) has no other
        // field that says so.
        //
        // ONLY WHEN THERE IS A GAP TO HAVE A REMEDY FOR. Written on the bare
        // flag, a failed index whose pages the render path all backfilled still
        // stamped the mark — and since the tooltip ranks this arm second, a run
        // whose real cause was an unplaceable match or a time limit read "the
        // document could not be fully indexed, so some pages were not searched"
        // over a page list of `none`, with the true cause and its correct
        // remedy both suppressed.
        ...(indexFailed &&
        (recordUnsearchedPages.length > 0 || pageCountUnknown)
          ? { indexFailed: true }
          : {}),
        // Streaming only (the pre-pass completion re-runs it). A failed index
        // would fail every open, so it stays plainly incomplete instead.
        ...(stillIndexing ? { indexing: true } : {}),
      };
      // A failed index whose every page still has text (the render path
      // backfilled what the pre-pass missed) left nothing unsearched. Writing
      // the mark anyway put up a chip that named no cause (VE-29686).
      return mark.indexing || hasIncompleteCause(mark) ? mark : undefined;
    };
    /**
     * Did ANYTHING about this run fail to finish? Only used to decide whether
     * the records are worth rewriting on a no-op run — the per-lexicon marks
     * above decide what each record actually says.
     */
    const incomplete =
      runWideIncomplete ||
      done.abandonedEntryIds.length > 0 ||
      unresolvedGeometry > 0;
    // Rewrite flagged records even on a no-op run, so a clean run clears them.
    const clearsIncomplete = current.appliedLexicons.some(
      (r) => lexiconIds.includes(r.lexiconId) && r.incomplete
    );
    // `indexing` marks this run clears (clean or not), for the audit follow-up.
    // Loaded lexicons only: a deleted one keeps its unsearched pages.
    const loadedIds = new Set(lexicons.map((l) => l.id));
    const completedIndexing = stillIndexing
      ? []
      : current.appliedLexicons
          .filter(
            (r) =>
              lexiconIds.includes(r.lexiconId) &&
              loadedIds.has(r.lexiconId) &&
              r.incomplete?.indexing
          )
          .map((r) => r.lexiconId);

    // How many matches THIS RUN redacted — reported by the merge rather than
    // counted off the merged cards, which is the distinction this used to get
    // wrong.
    //
    // It summed `autoRedactedMatchIds` across every card, and that field is
    // DURABLE by design: it records how a match first came to be redacted and
    // outlives the reviewer un-redacting it, the lexicon being removed, and the
    // document being reopened. So the total it produced was "everything this
    // document has ever auto-redacted", and any later apply inherited it — a
    // reviewer who removed an auto-redacting lexicon and applied it again got
    // the summary dialog a second time, reporting matches from the first run
    // and offering to Remove them, for a run that had auto-redacted nothing.
    //
    // A re-applied lexicon carries no `autoRedact` (the record was removed with
    // it, and `buildAppliedRecords` does not invent one), so this is now 0 for
    // that run and the matches go through review like any other.
    const autoRedactedMatchIds = merged.runAutoRedactedMatchIds;
    const autoRedactedCount = autoRedactedMatchIds.length;

    // BR-47's surface, reused: an entry whose matches could not be attributed
    // to any lexicon is exactly "could not be evaluated" from the reviewer's
    // side, and it must be REPORTED rather than dropped. Named once here so the
    // count and the lexicon names below cannot describe different sets.
    const abandonedEntryIds = [
      ...new Set([...done.abandonedEntryIds, ...merged.unattributedEntryIds]),
    ];

    yield* put(
      LEXICON_APPLY_SUCCESS({
        requestId,
        detections: merged.detections,
        autoRedactedCount,
        autoRedactedMatchIds,
        // On a no-op, carry the EXISTING records forward rather than rebuilding
        // them: a fresh `appliedAt` on every page-text arrival is itself churn,
        // and it would mark the session dirty through the back door.
        // `changed || incomplete`: a no-op run that did not FINISH still has a
        // durability signal to write. Gating on `changed` alone dropped it
        // exactly where it matters most — a long document whose re-evaluation
        // blows the apply budget adds no matches, so `changed` is false, and
        // the asset then asserted the lexicon was applied over pages that were
        // never searched (review-gate R4). The no-op guard exists to avoid
        // restamping `appliedAt`, not to lose the warning.
        appliedLexicons:
          changed || incomplete || clearsIncomplete
            ? [
                // `lexicons`, not `applicable`: a lexicon that loaded with zero
                // entries was still applied and must be settled (B5).
                ...buildAppliedRecords({
                  lexicons,
                  source: payload.source,
                  actor,
                  now,
                  evaluated: true,
                  existing: current.appliedLexicons,
                  // The file this run actually read. Compared against the
                  // session's current asset to know when it has gone stale.
                  evaluatedAssetId: current.mediaAssetId,
                  incompleteFor,
                  restamp: changed,
                }),
                // And the ids that never loaded at all — deleted between
                // selection and now. Nothing will ever load them.
                ...resolveUnloadedAsEvaluated({
                  existing: current.appliedLexicons,
                  requestedIds: lexiconIds,
                  loaded: lexicons,
                  now,
                }),
              ]
            : current.appliedLexicons,
        changed,
        // What the reviewer can actually SEE, not what the matcher found, and
        // the TOTAL on the document rather than this run's delta.
        //
        // Two wrong answers were tried here. `resolved.length` counts matches
        // the merge may then have dropped, so a failed attribution reported
        // "15 matches found" over an empty list. `representedMatchCount` fixed
        // that but counts only what THIS merge newly added — and every open
        // runs the apply more than once (text arrives in chunks, then the
        // pre-pass signals completion), with re-runs idempotent by design, so
        // the last run adds nothing and the panel announced "No matches found"
        // over a document full of cards.
        //
        // `documentMatchCount` is derived from the merged detections by the
        // same rule the detections list counts by, so the number in the panel
        // and the rows on screen are the same quantity by construction.
        matchCount: merged.documentMatchCount,
        disposition: {
          alreadyPresent: merged.alreadyPresentMatchIds.length,
          folded: merged.foldedMatchIds.length,
          dismissed: merged.skippedDismissedMatchIds.length,
          unattributed: merged.unattributedEntryIds.length,
          emptyLexicons: 0,
          unresolvedGeometry,
        },
        abandonedEntryIds,
        // WHERE those entries live, which is the only half of "which entry" the
        // reviewer can act on. The ids themselves are shown nowhere else in the
        // product, and the terms may not be shown here at all (NFR-S7 /
        // SEC-R3), so the panel prints these instead.
        //
        // `lexicons`, not `applicable`: an entry can be abandoned in a lexicon
        // that contributed no matches, and narrowing the search set would drop
        // its name while still counting its entry.
        abandonedLexiconNames: lexiconNamesForEntries(
          lexicons,
          abandonedEntryIds
        ),
        timedOut: done.timedOut,
        crashed: done.crashed ?? false,
        pagesMatched: done.pagesMatched,
        // A failed index won't fill in: list its gaps. Streaming: neutral note.
        unsearchedPages: indexFailed ? recordUnsearchedPages : unsearchedPages,
        ...(stillIndexing ? { indexing: true } : {}),
        ...(indexFailed && pageCountUnknown ? { pageCountUnknown: true } : {}),
        // The banner and the audit trail need the cause too, not just the row
        // (VE-29686). Same gate as the record's mark: a remedy is only worth
        // naming when something was actually missed.
        ...(indexFailed &&
        (recordUnsearchedPages.length > 0 || pageCountUnknown)
          ? { indexFailed: true }
          : {}),
        ...(completedIndexing.length > 0 ? { completedIndexing } : {}),
      })
    );

    // Put the reviewer where the results are (QA-7). Applied rows are
    // CANDIDATES, so they list in the detections tab — not in Redactions,
    // which shows only what has already been promoted. Without this the panel
    // announced "N matches found" and left the user on whatever tab they were
    // on; three separate rounds of "I applied it, where is it?" came from it.
    //
    // Only for an EXPLICIT editor apply: the upload path evaluates on first
    // open, and yanking the tab out from under someone who did not just ask
    // for it would be worse than the problem.
    if (payload.navigate) {
      yield* put(SET_DETECTIONS_TAB({ tab: 'detections' }));
      yield* put(SET_REDACTIONS_SUBTAB({ subtab: 'pii' }));
    } else if (autoRedactedCount > 0) {
      // AUTO REDACT LANDS ON REDACTIONS, and this is the one case where the
      // first-open path moves the reviewer.
      //
      // The landing rule asks whether the document has redaction work, and it
      // is evaluated when the document OPENS — before this run exists. A
      // document whose only redactions are the ones this run just made
      // therefore opened on Detections, correctly by that rule and wrongly for
      // the reviewer, who was looking at suggestions while the redactions sat
      // on the other tab. Moving them here is not the same as yanking the tab
      // from under an unasked-for run: something was redacted without being
      // asked, and that is precisely what they need to see.
      // `'all'` IS the Redactions tab: `effectiveDetectionsTopTab` reads only
      // `'pii'` as Detections, and the durable values predate both labels.
      yield* put(SET_REDACTIONS_SUBTAB({ subtab: 'all' }));
    }
  } catch (error: unknown) {
    yield* put(
      LEXICON_APPLY_FAILURE({
        requestId,
        message:
          error instanceof Error ? error.message : 'Lexicon apply failed',
      })
    );
  }
}

/**
 * Turns the matcher's character offsets into glyph geometry, page by page.
 *
 * On the main thread because that is where `pageTextItems` lives — the worker
 * has no pdfjs and no geometry model at all (ADR-002).
 */
function resolveMatchGeometry(
  session: SessionState,
  outcome: LexiconMatchOutcome
): {
  resolved: LexiconMatchWithText[];
  unresolved: number;
  /**
   * How many matches each ENTRY lost, keyed by entry id.
   *
   * The total alone could only ever be reported run-wide, which meant a
   * lexicon whose every match was placed still carried "matches that could not
   * be placed" because a different lexicon's had not been. Per entry, the
   * caller can hand each lexicon its own number.
   */
  unresolvedByEntry: Map<string, number>;
} {
  const byPage = new Map<number, LexiconMatchSpan[]>();
  for (const match of outcome.matches) {
    const list = byPage.get(match.page) ?? [];
    list.push(match);
    byPage.set(match.page, list);
  }

  const out: LexiconMatchWithText[] = [];
  /** Matches the matcher reported that no geometry could be produced for. */
  let unresolved = 0;
  const unresolvedByEntry = new Map<string, number>();
  const chargeEntry = (entryId: string, n = 1) => {
    if (n > 0) {
      unresolvedByEntry.set(entryId, (unresolvedByEntry.get(entryId) ?? 0) + n);
    }
  };
  for (const [page, spans] of [...byPage.entries()].sort(
    (a, b) => a[0] - b[0]
  )) {
    const text = session.pageTexts?.[page];
    const items = session.pageTextItems?.[page];
    if (!text || !items || items.length === 0) {
      // The page's data was dropped between matching and resolution. Skipping
      // is correct — geometry cannot be invented, and a detection with no rects
      // is worse than one that never appeared — but it must not be SILENT, so
      // the spans are counted and reported (review-gate B9). `items.length`
      // is checked because an empty array is truthy.
      unresolved += spans.length;
      for (const span of spans) {
        chargeEntry(span.entryId);
      }
      continue;
    }
    const placed = resolveLexiconMatchGeometry(spans, text, items);
    // `resolveLexiconMatchGeometry` also drops a span whose glyph range yields
    // no rectangle; that gap is real and belongs in the same count.
    unresolved += spans.length - placed.length;
    // Per entry, by difference: the resolver returns what it placed, so what
    // an entry LOST is what it offered minus what came back. Counting the
    // placed spans rather than diffing identities keeps this independent of
    // whether the resolver returns the same objects it was given.
    if (placed.length !== spans.length) {
      const offered = new Map<string, number>();
      for (const span of spans) {
        offered.set(span.entryId, (offered.get(span.entryId) ?? 0) + 1);
      }
      for (const span of placed) {
        offered.set(span.entryId, (offered.get(span.entryId) ?? 0) - 1);
      }
      offered.forEach((lost, entryId) => chargeEntry(entryId, lost));
    }
    out.push(...placed);
  }
  return { resolved: out, unresolved, unresolvedByEntry };
}

/**
 * Lexicons whose selection has been recorded but never evaluated (BR-50), those
 * applied earlier in this session (BR-48), those evaluated against a FILE
 * THIS DOCUMENT NO LONGER IS, and those evaluated before the text index
 * finished (`incomplete.indexing`).
 *
 * The third is what carries a lexicon across OCR. A run on the pre-OCR file
 * matches nothing and stamps `evaluatedAt`, after which the first two rules
 * both pass it over — `watchFirstOpen` only takes unevaluated records, and a
 * fresh session starts with an empty `sessionAppliedLexiconIds`. So the reload
 * onto the searchable copy left the zero-match result standing over a document
 * that now had text.
 *
 * Comparing asset ids makes that derivable instead of a flag on the reload
 * path, and re-running is safe by construction: BR-43's deterministic match
 * ids, M3's honoured dismissals and M1's untouched existing work are what make
 * re-evaluation idempotent, which is why BR-48 re-runs the whole set rather
 * than tracking which pages each lexicon has seen.
 *
 * BOTH IDS MUST BE KNOWN to call something stale. A record from before
 * `evaluatedAssetId` existed has no id to compare, and a session that has not
 * resolved its own asset has nothing to compare against — treating either as a
 * mismatch would re-evaluate every lexicon on every open of a document that has
 * not changed at all.
 */
export function pendingLexiconIds(session: SessionState): string[] {
  const unevaluated = session.appliedLexicons
    .filter((r) => !r.evaluatedAt)
    .map((r) => r.lexiconId);
  const staleAgainstThisFile = session.appliedLexicons
    .filter((r) => evaluatedAgainstOtherFile(r, session.mediaAssetId))
    .map((r) => r.lexiconId);
  const provisional = session.appliedLexicons
    .filter((r) => r.incomplete?.indexing === true)
    .map((r) => r.lexiconId);
  return [
    ...new Set([
      ...unevaluated,
      ...staleAgainstThisFile,
      ...provisional,
      ...session.lexiconApply.sessionAppliedLexiconIds,
    ]),
  ];
}

/** Both asset ids must be known to call a record stale (see above). */
function evaluatedAgainstOtherFile(
  record: AppliedLexiconRecord,
  currentAssetId: string | null | undefined
): boolean {
  return (
    !!currentAssetId &&
    !!record.evaluatedAt &&
    !!record.evaluatedAssetId &&
    record.evaluatedAssetId !== currentAssetId
  );
}

/**
 * Whether the record is a finished evaluation of THIS file. While the pre-pass
 * streams, only lexicons without one re-run per chunk; otherwise each chunk
 * completed a full apply — ~65 per 1,602-page open (VE-29502).
 */
function hasCurrentEvaluation(session: SessionState, lexiconId: string) {
  const record = session.appliedLexicons.find((r) => r.lexiconId === lexiconId);
  return (
    !!record?.evaluatedAt &&
    !evaluatedAgainstOtherFile(record, session.mediaAssetId)
  );
}

/**
 * First-open evaluation (BR-50).
 *
 * Runs unconditionally when an unevaluated record exists, even before any text
 * is extracted: the run records the ids in `sessionAppliedLexiconIds` and
 * settles into `awaiting-text`, and the page-text watcher below picks it up.
 */
function* watchFirstOpen() {
  yield* takeLatest(OPEN_DOCUMENT_SUCCESS, function* () {
    const session = yield* select(selectSession);
    if (!session) {
      return;
    }
    const unevaluated = session.appliedLexicons
      .filter((r) => !r.evaluatedAt)
      .map((r) => r.lexiconId);
    if (unevaluated.length === 0) {
      return;
    }
    yield* runLexiconApply({
      lexiconIds: unevaluated,
      // The selection was made at upload; this run is that application
      // finally happening, not a new editor-initiated one (BR-51).
      source: 'upload',
    });
  });
}

/**
 * Re-evaluation as page text arrives (BR-48).
 *
 * Re-runs the whole applied set over every extracted page rather than tracking
 * which pages each lexicon has already seen. The tracking version was written
 * and rejected: with several lexicons applied at different times it needs a
 * per-lexicon page set, and getting it wrong silently under-redacts. Re-running
 * is idempotent by construction (BR-43 — deterministic match ids, M3 honours
 * dismissals, M1 never touches existing work), so the cost of the simple
 * version is duplicated matching work, and the cost of the clever one is a
 * missed redaction.
 */
function* watchPageTextArrival() {
  const reevaluate = function* () {
    const session = yield* select(selectSession);
    if (!session) {
      return;
    }
    // The same gate `runLexiconApply` defers on. Without it this watcher would
    // walk straight past the deferral on the next text burst.
    const gate = yield* select(selectPiiRunGate);
    if (gate !== 'open') {
      return;
    }
    const pending = pendingLexiconIds(session);
    if (pending.length === 0) {
      // NOTHING IS COMING. This is the one place that can say so, because it is
      // the one place that computes staleness against the session that actually
      // opened — so if a reload raised the "updating matches" notice and turned
      // out to have nothing to re-run, this is what lowers it. Doing it in the
      // reducer meant guessing inside `OPEN_DOCUMENT_SUCCESS`, where the
      // session is still the OUTGOING document.
      yield* put(OCR_RELOAD_SETTLED());
      return;
    }
    if (pagesWithText(session).length === 0) {
      // Deliberately NOT settled: text may still arrive, and a run with it.
      return;
    }
    // `source` here is only a fallback for a record that does not exist yet;
    // `buildAppliedRecords` preserves an existing record's source so an
    // upload-selected lexicon stays upload-selected (BR-51).
    yield* runLexiconApply({
      lexiconIds: pending,
      source: 'editor',
      navigate: false,
    });
  };

  // SET_PAGE_TEXT_ITEMS is watched too: `pagesWithText` requires geometry as
  // well as text, so a page whose items arrive separately must trigger the
  // re-evaluation that will finally place its matches.
  /**
   * ONE WATCHER, so the triggers cannot race each other.
   *
   * These were three separate `takeLatest`s — the text bursts, the scan's
   * completion and the pre-pass's — and `takeLatest` only cancels its OWN
   * previous run. Three of them meant three `runLexiconApply`s able to be in
   * flight at once over the same document. It was survivable because re-running
   * is idempotent by construction (BR-43 / M1 / M3), which is the only reason
   * it was not a bug, but it was three times the matching work and three
   * chances for the merge to interleave.
   *
   * Folded into one, the last trigger wins and the earlier run is cancelled —
   * which is the behaviour the pre-pass watcher was already written around
   * ("in case the batch it publishes lands while a run is already in flight and
   * gets cancelled by takeLatest"). Every trigger ends in a `reevaluate`
   * except a text burst held during the pre-pass; those rely on
   * PDF_TEXT_PREPASS_DONE, which fires once per open, even on failure.
   */
  yield* takeLatest(
    [
      SET_PAGE_TEXT,
      SET_PAGE_TEXT_BATCH,
      SET_PAGE_TEXT_ITEMS,
      // The gate opening is its own trigger, and it has to be: the text bursts
      // all land during the pre-pass, which finishes BEFORE the scan that
      // decides whether this document needs OCR. A lexicon deferred while that
      // scan ran would sit deferred for ever, because the last thing that would
      // have woken it had already happened.
      PAGE_SEARCHABILITY_SCAN_DONE,
      // The pre-pass's own completion signal, in case the batch it publishes
      // lands while a run is already in flight and gets cancelled.
      PDF_TEXT_PREPASS_DONE,
      // AND EVERY WAY THE OCR STATE CAN SETTLE. A fresh session starts at
      // `checking`, which the gate refuses on — so an apply made at open
      // defers, and if the poller answers AFTER the pre-pass and the scan have
      // both finished, every other trigger here has already fired and the
      // deferral would strand. These are the actions that move the state out
      // of `checking`; between them and the two above, whichever of the engine
      // and the index finishes last wakes the run.
      OCR_FAILED,
      OCR_INDETERMINATE,
      OCR_MANIFEST_LOADED,
      OCR_NOT_RUN,
      OCR_PROCESSING,
      OCR_REFRESH_AVAILABLE,
      OCR_TIMED_OUT,
    ],
    function* (action: { type: string }) {
      // Debounced only for the text bursts, which arrive per chunk. The two
      // completion signals fire once and should not be made to wait.
      const isTextBurst =
        action.type === SET_PAGE_TEXT.type ||
        action.type === SET_PAGE_TEXT_BATCH.type ||
        action.type === SET_PAGE_TEXT_ITEMS.type;
      if (isTextBurst) {
        yield* delay(PAGE_TEXT_SETTLE_MS);
      }

      /**
       * The only place `awaiting-text` can be resolved for a document with no
       * extractable text at all. `reevaluate` bails when there are no pages
       * with text, so on a scan the status stayed `awaiting-text` for ever —
       * and that counts as "running" in the panel, which left Apply disabled
       * with no way back.
       *
       * "If there is still no text there never will be" is TRUE ONLY WITH THE
       * GATE OPEN and the index finished. While OCR is working on a document
       * whose pages need it, text is precisely what is coming — just not into
       * this session, and not until the reload. Failing there would turn a
       * deliberate deferral into an error the reviewer cannot act on, on
       * exactly the documents the deferral exists for.
       *
       * Checked on every trigger rather than only the completion ones: it costs
       * two selects, and gating it by action type is how the condition and the
       * trigger list drift apart later.
       *
       * AND IT SAYS WHICH OF THE TWO IT IS (VE-29686). "No text to search" is a
       * claim about the DOCUMENT, and it is false when the index simply broke:
       * a pre-pass that gives up on its first range yields nothing, exactly
       * like a scan, and if nothing has rendered to backfill a page the two
       * are indistinguishable HERE — only `textIndexFailed` tells them apart.
       * Reported as a scan, the reviewer was sent to OCR a document that has a
       * perfectly good text layer, and the remedy that works (reopen, which
       * re-runs the pre-pass) was never offered.
       */
      const session: SessionState | null = yield* select(selectSession);
      const gate = yield* select(selectPiiRunGate);
      if (
        session &&
        session.lexiconApply.status === 'awaiting-text' &&
        gate === 'open' &&
        session.textIndexReady &&
        pagesWithText(session).length === 0
      ) {
        yield* put(
          LEXICON_APPLY_FAILURE({
            requestId: session.lexiconApply.requestId ?? '',
            message: (session.textIndexFailed === true
              ? 'indexFailed'
              : 'noExtractableText') satisfies LexiconApplyErrorCode,
          })
        );
        return;
      }
      const pendingNow = session ? pendingLexiconIds(session) : [];
      if (
        isTextBurst &&
        session &&
        !session.textIndexReady &&
        pendingNow.length > 0 &&
        pendingNow.every((id) => hasCurrentEvaluation(session, id))
      ) {
        // Everything pending already has results; PDF_TEXT_PREPASS_DONE
        // re-runs the lot.
        return;
      }
      yield* reevaluate();
    }
  );
}

/** Hoisted so `call`'s argument inference keeps the literal type. */
const UPLOAD_WRITER: RedactionAssetWriter = 'upload';

/**
 * Records the upload dialog's lexicon selection against a freshly created
 * document (`U5-FR14` / BR-49), called by the uploads saga per PDF TDO with
 * the file's upload URL.
 *
 * The selection is read from the BATCH the overview saga staged for this
 * upload (`STAGE_UPLOAD_LEXICON_BATCH`), keyed by upload URL — not from the
 * live picker selection. Consuming the live selection here and clearing it on
 * first use recorded a multi-file upload's selection against the FIRST TDO
 * only, silently giving files 2..n none of it (review-gate finding 2). A URL
 * outside the batch records nothing, which is what keeps a later upload or a
 * URL-ingested TDO from inheriting a stale selection (review finding 3's
 * other direction; the picker still clears on mount).
 *
 * The record is an INITIAL `document-redaction-data` asset carrying only
 * `appliedLexicons` (each with no `evaluatedAt`) — the same asset the editor
 * loads at open, so first-open evaluation (BR-50) needs no second read path.
 * Nothing is evaluated here: matching needs extracted page text, which does
 * not exist until the editor opens the document (ADR-004), and the upload
 * path's latency budget forbids it anyway (`NFR9` / `NFR-U5-4`).
 *
 * Best-effort by contract: lexicons are additive and MUST NOT gate upload.
 * The caller decides how to surface a failure; nothing here logs a term or a
 * lexicon name (SEC-R3 — an ops log is the one place even names stay out of).
 */
export function* recordUploadLexiconSelection(tdoId: TDOId, fileUrl: string) {
  const batch = yield* select(selectUploadLexiconBatch);
  if (!batch || !fileUrl || !batch.fileUrls.includes(fileUrl)) {
    return;
  }
  const lexiconIds: readonly string[] = batch.lexiconIds;
  if (lexiconIds.length === 0) {
    return;
  }
  // From the BATCH, like the ids themselves — the live selection may already
  // have moved on to the next upload by the time this file's TDO exists.
  const autoRedactIds = new Set(batch.autoRedactLexiconIds ?? []);
  // This file's share of the batch is consumed; the batch clears itself once
  // every staged URL has been. A failed CREATE_TDO never consumes its URL —
  // the leftover is replaced when the next batch is staged.
  yield* put(CONSUME_UPLOAD_LEXICON_BATCH_FILE({ fileUrl }));

  const user = (yield* select((s: unknown) => selectUser(s as never))) as
    | { email?: string }
    | undefined;
  const now = new Date().toISOString();

  // Names + revisions come from the lexicons the selector displayed — the
  // list the user actually chose from. A lexicon deleted between selection
  // and upload simply drops out (nothing to record for it).
  const byId = yield* select((s: unknown) =>
    selectLexiconsById(s as Parameters<typeof selectLexiconsById>[0])
  );
  const records: AppliedLexiconRecord[] = lexiconIds.flatMap((lexiconId) => {
    const lexicon = byId[lexiconId];
    if (!lexicon) {
      return [];
    }
    return [
      {
        lexiconId,
        lexiconName: lexicon.name,
        revision: lexicon.revision,
        appliedAt: now,
        appliedBy: user?.email ?? '',
        source: 'upload' as const,
        // Recorded per document, and read from here at first open rather than
        // from the library — a flag flipped in settings afterwards must not
        // change the account of a run that already happened (U5-FR2). Only
        // written when true, so a record looks exactly as it did before this
        // field for every lexicon that is not auto-redacting.
        ...(autoRedactIds.has(lexiconId) ? { autoRedact: true } : {}),
        // The BR-50 marker: first editor open sees null and evaluates.
        evaluatedAt: null,
      },
    ];
  });
  if (records.length === 0) {
    return;
  }

  const token = yield* select(selectToken);
  const cfg = yield* select(selectGraphQLConfig);
  // Object form since master's VE-28061 refactor. Only the two fields this
  // write is about are named; everything else defaults to empty, which is the
  // point of that change — the old positional call passed five empty arrays to
  // reach the seventh slot.
  const body = redactionsToAssetBody({
    redactions: [],
    tdoId,
    appliedLexicons: records,
  });
  yield* call(
    serviceSaveDocumentRedactionData,
    tdoId,
    body,
    token,
    cfg.apiRoot,
    cfg.graphQLEndpoint,
    // No burn-in version: nothing is being exported.
    undefined,
    // Marked as written by the upload pipeline (review B4). Without it this
    // asset reads as a save the reviewer made, and `openDocument` would hand
    // both auto-apply sagas a non-null `lastSavedAt` on a document nobody has
    // opened — suppressing the `autoApply` lexicons AND VE-24693's watermarks
    // on the one open they exist for.
    UPLOAD_WRITER
  );
}

export function* lexiconApplySaga() {
  yield* fork(watchApplyRequested);
  yield* fork(watchFirstOpen);
  yield* fork(watchPageTextArrival);
  yield* fork(watchUploadLexiconSelection);
}

/**
 * Owns the upload-selection write (review-gate M11).
 *
 * The uploads saga used to `call` `recordUploadLexiconSelection` directly,
 * which made it a second writer of an asset — and of a history rotation — this
 * module owns. It now dispatches `RECORD_UPLOAD_LEXICON_SELECTION` and this
 * watcher does the write, including the user-facing failure. `takeEvery`, not
 * `takeLatest`: a multi-file upload records one selection per TDO and they
 * must not cancel each other.
 */
function* watchUploadLexiconSelection() {
  yield* takeEvery(
    RECORD_UPLOAD_LEXICON_SELECTION,
    function* ({ payload }: { payload: { tdoId: string; fileUrl: string } }) {
      try {
        yield* call(
          recordUploadLexiconSelection,
          payload.tdoId as TDOId,
          payload.fileUrl
        );
      } catch {
        // Lexicons are additive, so a failure here must not read as an upload
        // failure. The message names no lexicon — SEC-R3 keeps even names off
        // ops surfaces.
        yield* put(
          enqueueSnackbar({
            message:
              'The file uploaded, but its lexicon selection could not be ' +
              'saved. Apply lexicons from the editor instead.',
          })
        );
      }
    }
  );
}

/**
 * `takeLatest` so a second apply cancels the first rather than racing it: two
 * runs merging into the same session would each read the pre-merge detection
 * list, and the loser's result would drop the winner's matches.
 */
function* watchApplyRequested() {
  yield* takeLatest(APPLY_LEXICONS_TO_OPEN_DOCUMENT, function* ({ payload }) {
    // A run in flight stops without merging once this one starts: take over
    // its lexicons (NFR-S3). Only then — otherwise it would restamp them.
    const session: SessionState | null = yield* select(selectSession);
    const inFlight =
      session?.lexiconApply.status === 'running'
        ? pendingLexiconIds(session)
        : [];
    // The reviewer just pressed Apply, so this is the one run that navigates.
    yield* runLexiconApply({
      ...payload,
      lexiconIds: [...payload.lexiconIds, ...inFlight],
      navigate: true,
    });
  });
}
